export type Role = "admin" | "technician" | "viewer";

export interface User {
  id: number;
  username: string;
  email: string;
  role: Role;
  is_active: boolean;
  must_change_password?: boolean;
  can_view_billing?: boolean;
  totp_enabled?: boolean;
  /** Idle sign-out limit in minutes. null = appliance default, 0 = never. */
  idle_timeout_minutes?: number | null;
  mfa_required?: boolean;
  /** Feature keys this user's ROLE may use (Admin → Roles & access). Distinct
   * from LicenseContext.hasFeature, which is about what the MSP has paid for.
   * Present on /me and the login response only; absent on list endpoints. */
  features?: string[] | null;
  /** {feature key: route prefixes}. Sent alongside `features` so the browser
   * resolves a route to a feature using the server's catalogue rather than a
   * second copy that would drift. */
  feature_routes?: Record<string, string[]> | null;
  skills?: string[] | null;
  is_sso?: boolean;
  created_at: string;
  last_login?: string;
  /** Origin of this user's one active session (single-session enforcement).
   * Null/absent when they have no live session — e.g. never signed in since
   * the feature shipped, or an admin force-signed them out. */
  /** False once the session was revoked or timed out — the origin fields below
   *  persist after sign-out, so they cannot be used to infer "signed in". */
  session_active?: boolean;
  session_started_at?: string | null;
  session_ip?: string | null;
  session_location?: string | null;
  session_user_agent?: string | null;
}

/** Discriminated union returned by AuthContext.login. */
export type LoginResult =
  | { requires2fa: false }
  | { requires2fa: true; partialToken: string; availableFactors: string[] };

export interface ApiToken {
  id: number;
  name: string;
  prefix: string;
  expires_at: string | null;
  last_used_at: string | null;
  last_used_ip: string | null;
  revoked_at: string | null;
  created_at: string;
}

export interface ApiTokenCreateResponse extends ApiToken {
  /** Plaintext token. Returned ONLY at creation — never queryable again. */
  token: string;
}

// "teams_graph" posts through the customer's own Microsoft 365 connection
// rather than a webhook URL - it is the only kind with no URL.
export type WebhookKind = "slack" | "teams" | "teams_graph" | "pagerduty" | "generic";

export const WEBHOOK_EVENT_TYPES = [
  "alert.critical",
  "alert.warning",
  "alert.info",
  "sla.response_breach",
  "sla.resolution_breach",
] as const;

export type WebhookEventType = (typeof WEBHOOK_EVENT_TYPES)[number];

export interface Webhook {
  id: number;
  name: string;
  kind: WebhookKind;
  customer_id?: number | null;
  target_team_id?: string | null;
  target_channel_id?: string | null;
  target_label?: string | null;
  url: string;
  integration_key: string | null;
  event_filter: string[];
  enabled: boolean;
  last_success_at: string | null;
  last_failure_at: string | null;
  last_error: string | null;
  created_at: string;
  updated_at: string;
}

export interface WebhookTestResponse {
  success: boolean;
  error: string | null;
}

export interface Customer {
  id: number;
  name: string;
  domain?: string;
  primary_contact?: string;
  contact_email?: string;
  contact_phone?: string;
  address?: string;
  notes?: string;
  is_active: boolean;
  created_at: string;
  device_count: number;
  online_count: number;
  default_billable?: boolean;
  hourly_rate_cents?: number;
  billing_email?: string;
  sales_tax_pct?: number;
  portal_can_view_invoices?: boolean;
  huntress_org_key?: string;
  acronis_tenant_id?: string | null;
  dnsfilter_site_id?: string | null;
  ai_auto_close_enabled?: boolean;
  ai_auto_close_cool_off_hours?: number;
}

export interface DeviceGroup {
  id: number;
  customer_id: number;
  name: string;
  description?: string;
  created_at: string;
  device_count: number;
}

export interface Device {
  id: number;
  device_uuid: string;
  customer_id?: number;
  group_id?: number;
  hostname: string;
  ip_address?: string;
  mac_address?: string;
  os_version?: string;
  os_build?: string;
  platform?: "windows" | "macos" | "linux" | null;
  last_seen?: string;
  status: "online" | "offline";
  maintenance_mode?: boolean;
  /** Whether alerting is suppressed RIGHT NOW, per the alert evaluator's
   *  own rule. NOT the same as maintenance_mode, which outlives its
   *  expiry - 60 devices carried a stale flag and the console captioned
   *  them "alerting muted" while they were alerting normally. */
  in_maintenance?: boolean;
  maintenance_until?: string | null;
  agent_version?: string;
  logged_in_user?: string | null;
  pending_reboot: boolean;
  gotoassist_installed: boolean;
  huntress_installed?: boolean;
  is_ad_runner?: boolean;
  // Vendor integration status — populated by hourly syncs. NULL when
  // the relevant vendor isn't configured for this customer.
  edr_status?: "secure" | "at_risk" | "breached" | "unmanaged" | "unknown" | null;
  edr_last_synced_at?: string | null;
  huntress_agent_id?: string | null;
  backup_status?: "success" | "partial" | "failed" | "running" | "no_recent" | "never" | "unmanaged" | null;
  backup_last_run_at?: string | null;
  backup_last_success_at?: string | null;
  backup_last_synced_at?: string | null;
  acronis_resource_id?: string | null;
  created_at: string;
  customer_name?: string;
  group_name?: string;
  last_disk_health?: { model?: string; health?: string; operational?: string; serial?: string; media?: string }[] | null;
  last_battery_health_pct?: number | null;
  has_battery?: boolean | null;
  last_hw_health_at?: string | null;
  // Live metrics + wave-A/B hardware extras
  stale_duplicate?: boolean;
  last_cpu_percent?: number | null;
  last_memory_percent?: number | null;
  top_processes?: {
    by_cpu?: { pid: number; name: string; cpu: number; mem_mb: number }[];
    by_mem?: { pid: number; name: string; cpu: number; mem_mb: number }[];
  } | null;
  last_metrics_at?: string | null;
  last_cpu_temp_c?: number | null;
  last_chassis_temp_c?: number | null;
  last_fan_status?: { name?: string; rpm?: number | null; status?: string }[] | null;
  last_ups_status?: {
    present?: boolean;
    name?: string | null;
    status?: string;
    charge_pct?: number | null;
    runtime_minutes?: number | null;
  } | null;
  last_disk_io?: {
    per_disk?: { name: string; read_latency_ms: number; write_latency_ms: number; queue_length: number }[];
    max_latency_ms?: number;
  } | null;
  last_raid_status?: {
    controllers?: {
      vendor?: string;
      id?: string;
      model?: string;
      status?: string;
      virtual_disks?: { id?: string; raid_level?: string; state?: string; size_gb?: number }[];
      physical_disks?: { slot?: string; state?: string; model?: string; size_gb?: number }[];
    }[];
  } | null;
  last_psu_status?: {
    psus?: { id?: string; status?: string; watts?: number | null }[];
    /** null = not applicable (a single supply has no redundancy to lose). */
    redundancy_ok?: boolean | null;
    source?: "ipmi_wmi" | "omsa" | "ipmitool" | null;
    /** Why the list is empty. A guest HAS no supplies; a server that cannot
     *  be read is a gap. The console must not render those the same way. */
    reason?:
      | "virtual_machine"
      | "no_bmc"
      | "bmc_unreachable"
      | "no_psu_sensors"
      | "no_source"
      | "collector_error"
      | null;
  } | null;
  cpu_high_since?: string | null;
  identity_join_type?: "ad" | "entra" | "hybrid" | "workgroup" | "unknown" | null;
  domain_name?: string | null;
  /** "ready" | "notready" | "absent" | "unknown"; null = never collected.
   *  Never treat null/"unknown" as "absent" — that would grey out Enable
   *  BitLocker on machines that encrypt perfectly well. */
  tpm_state?: string | null;
  entra_tenant_id?: string | null;
  entra_device_id?: string | null;
  last_identity_at?: string | null;
}

export interface HardwareInfo {
  id: number;
  device_id: number;
  manufacturer?: string;
  model?: string;
  serial_number?: string;
  cpu_model?: string;
  cpu_cores?: number;
  /** Logical processors. Null on devices whose agent predates 1.0.78. */
  cpu_threads?: number;
  /** Physical packages; only worth showing when > 1. */
  cpu_sockets?: number;
  ram_gb?: number;
  disk_total_gb?: number;
  disk_free_gb?: number;
  bios_version?: string;
  nics?: {
    name?: string;
    description?: string;
    mac?: string;
    status?: string;
    speed?: string;
    dhcp?: string;
    ipv4?: string[];
    ipv6?: string[];
  }[];
  security_products?: { name: string; type: string; source?: string }[];
  backup_products?: { name: string }[];
  updated_at: string;
}

export interface SoftwareItem {
  id: number;
  device_id: number;
  name: string;
  version?: string;
  publisher?: string;
  install_date?: string;
}

export interface OnboardingPackage {
  id: number;
  customer_id: number;
  group_id?: number;
  os_type?: string; // "windows" | "macos" | "linux"
  token: string;
  download_count: number;
  created_at: string;
  download_url?: string;
}

export interface Summary {
  total_devices: number;
  online_devices: number;
  offline_devices: number;
  pending_reboot: number;
  open_tickets: number;
  total_customers: number;
}

export interface HealthSnapshot {
  active_alerts: {
    critical: number;
    warning: number;
    info: number;
    total: number;
    /** Unresolved but acknowledged — seen by a human, not yet fixed. */
    acknowledged?: number;
  };
  offline_devices: {
    total: number;
    recent: Array<{
      device_id: number;
      hostname: string;
      customer_name: string | null;
      last_seen: string | null;
    }>;
  };
  failed_remediation: Array<{
    device_id: number | null;
    device_hostname: string | null;
    customer_name: string | null;
    action: string;
    target: string | null;
    error_message: string | null;
    created_at: string;
  }>;
  network_down: NetworkHealthRow[];
  /** Up, but AeroGauge cannot read them. A monitoring gap, NOT an outage. */
  network_unmonitored: NetworkHealthRow[];
}

export interface NetworkHealthRow {
  id: number;
  name: string;
  ip_address: string;
  kind: string;
  customer_name: string | null;
  last_status: string;
  last_error?: string | null;
  last_polled_at: string | null;
}

export interface DomainUser {
  id: number;
  customer_id: number;
  sam_account_name: string;
  user_principal_name?: string;
  display_name?: string;
  given_name?: string;
  surname?: string;
  email?: string;
  title?: string;
  department?: string;
  description?: string;
  office?: string;
  office_phone?: string;
  mobile_phone?: string;
  company?: string;
  proxy_addresses?: string[];
  mail_nickname?: string;
  // The full DN. Shown as the organisational unit in the expanded attribute
  // panel - the container is the part anyone wants, not the whole string.
  distinguished_name?: string;
  enabled: boolean;
  locked: boolean;
  last_logon?: string;
  when_created?: string;
  last_synced?: string;
}

// AD attribute edit. Only the fields the user changed are sent; an empty string
// clears that attribute (where AD allows). Maps to PATCH /api/v1/ad/users/{id}.
export interface ADUpdateRequest {
  given_name?: string;
  surname?: string;
  display_name?: string;
  email?: string;
  title?: string;
  department?: string;
  description?: string;
  office?: string;
  office_phone?: string;
  mobile_phone?: string;
  company?: string;
  user_principal_name?: string;
  mail_nickname?: string;
  proxy_addresses?: string[];
}

export interface CommandQueuedResponse {
  command_id: number;
  status: string;
  generated_password?: string;
}

export interface AgentCommand {
  id: number;
  device_id: number;
  command_type: string;
  status: string;
  result?: Record<string, any>;
  error_message?: string;
  created_at: string;
  sent_at?: string;
  completed_at?: string;
}

export interface AuditEntry {
  id: number;
  actor_user_id?: number | null;
  actor_username?: string | null;
  customer_id?: number | null;
  action: string;
  target_type?: string | null;
  target_id?: string | null;
  target_label?: string | null;
  details?: Record<string, any> | null;
  result: string;
  error_message?: string | null;
  created_at: string;
}

export interface AuditLogPage {
  items: AuditEntry[];
  total: number;
  limit: number;
  offset: number;
}

export interface OffboardOptions {
  disable_account: boolean;
  reset_password: boolean;
  revoke_sessions: boolean;
  move_to_disabled_ou: boolean;
  strip_group_memberships: boolean;
  disabled_ou_dn?: string;
  note?: string;
}

export interface ServiceWatch {
  id: number;
  customer_id: number;
  service_name: string;
  display_name?: string;
  enabled: boolean;
  max_restarts_per_hour: number;
  created_at: string;
}

export interface RemediationEvent {
  id: number;
  customer_id?: number;
  device_id?: number;
  action: string;
  target?: string;
  result: "success" | "failed" | "skipped" | "rate_limited" | string;
  details?: Record<string, any>;
  duration_ms?: number;
  error_message?: string;
  created_at: string;
}

export interface TimeEntry {
  id: number;
  customer_id: number;
  ticket_id?: number;
  user_id?: number;
  user_username?: string;
  description?: string;
  started_at: string;
  ended_at?: string;
  duration_minutes?: number;
  billable: boolean;
  rate_cents_snapshot: number;
  created_at: string;
  updated_at: string;
}

export interface TimeTotals {
  customer_id: number;
  period_start: string;
  period_end: string;
  billable_minutes: number;
  non_billable_minutes: number;
  billable_amount_cents: number;
  tax_pct: number;
  tax_amount_cents: number;
  total_with_tax_cents: number;
  entry_count: number;
}

export interface CustomerNote {
  id: number;
  customer_id: number;
  user_id?: number;
  author_username?: string;
  body: string;
  created_at: string;
}

export type TicketStatus = "open" | "in_progress" | "on_hold" | "resolved" | "closed";
export type TicketPriority = "low" | "medium" | "high" | "urgent";

export interface HoldCategory {
  id: number;
  name: string;
  description?: string;
  created_at: string;
}

export interface Ticket {
  id: number;
  customer_id: number;
  customer_name?: string;
  device_id?: number;
  device_hostname?: string;
  title: string;
  description?: string;
  notify_email?: string;
  is_muted: boolean;
  priority: TicketPriority;
  status: TicketStatus;
  hold_category_id?: number;
  hold_category_name?: string;
  created_by_user_id?: number;
  created_by_username?: string;
  assigned_to_user_id?: number;
  assigned_to_username?: string;
  created_at: string;
  updated_at: string;
  resolved_at?: string;
  closed_at?: string;
  comment_count: number;
  time_logged_minutes: number;
  active_seconds: number;
  clock_running: boolean;
  waiting_on_customer: boolean;
  current_status_since?: string;
  /**
   * Set once this ticket has been merged into another. The row is kept rather
   * than deleted so old links and quoted ticket numbers still resolve, so the
   * UI has to say plainly that the conversation moved.
   */
  merged_into_ticket_id?: number | null;
}

export interface TicketComment {
  id: number;
  ticket_id: number;
  user_id?: number;
  author_username?: string;
  body: string;
  created_at: string;
  /**
   * Staff-only note: not emailed to the customer and never returned by the
   * portal. Optional because the portal's own responses omit it entirely — a
   * customer session can never receive an internal row in the first place.
   */
  is_internal?: boolean;
}

export interface PortalDevice {
  id: number;
  hostname: string;
  status: string;
  last_seen?: string;
  pending_reboot?: boolean;
}

export interface PortalInvoiceSummary {
  period_label: string;
  period_start: string;
  period_end: string;
  billable_minutes: number;
  non_billable_minutes: number;
  billable_amount_cents: number;
  entry_count: number;
}

export interface PortalCustomerUser {
  id: number;
  customer_id: number;
  email: string;
  full_name?: string;
  is_active: boolean;
  role?: string; // "administrator" | "user"
  must_change_password?: boolean;
  can_view_invoices?: boolean;
  last_login?: string;
  created_at: string;
}

export interface LocalDrive {
  letter: string;
  label?: string;
  file_system?: string;
  drive_type?: string;
  total_gb?: number;
  free_gb?: number;
}
export interface MappedDrive {
  letter: string;
  remote_path: string;
  username?: string;
  persistent: boolean;
}
export interface SharedFolder {
  name: string;
  path?: string;
  description?: string;
}
export interface StorageReport {
  device_id: number;
  local_drives: LocalDrive[];
  mapped_drives: MappedDrive[];
  shared_folders: SharedFolder[];
  updated_at?: string;
}

export interface AlertThreshold {
  id: number;
  customer_id?: number;
  metric: string;
  info_pct: number;
  warn_pct: number;
  critical_pct: number;
  re_alert_hours: number;
  updated_at: string;
}

export interface Alert {
  id: number;
  customer_id?: number;
  customer_name?: string;
  device_id: number;
  device_hostname?: string;
  metric: string;
  target?: string;
  severity: "info" | "warning" | "critical" | string;
  value?: number;
  message?: string;
  created_at: string;
  updated_at: string;
  last_email_at?: string;
  acknowledged_at?: string;
  acknowledged_by_user_id?: number;
  resolved_at?: string;
}

export interface DevicePatch {
  id: number;
  device_id: number;
  update_id: string;
  source: "wua" | "winget" | string;
  title: string;
  kb?: string | null;
  severity: string;
  categories: { name: string; type?: string }[];
  reboot_required: boolean;
  is_downloaded: boolean;
  status: "available" | "installed" | "failed" | string;
  approval_state: "pending" | "approved" | "declined" | "auto_approved" | "auto_declined" | string;
  seen_at: string;
  install_date?: string | null;
  install_attempted_at?: string | null;
  install_command_id?: number | null;
  error_message?: string | null;
  reboot_done_at?: string | null;
}

export interface M365User {
  id: number;
  customer_id?: number | null;
  object_id: string;
  upn: string;
  display_name?: string | null;
  given_name?: string | null;
  surname?: string | null;
  account_enabled: boolean;
  on_premises_sync_enabled?: boolean | null;
  license_skus: string[];
  mfa_methods: { id?: string; type?: string }[];
  is_mfa_capable: boolean;
  /** M365 directory roles held. null = never collected (NOT the same as []). */
  admin_roles?: string[] | null;
  last_sync_at: string;
}

export interface M365Subscription {
  id: number;
  customer_id?: number | null;
  sku_id: string;
  sku_part_number: string;
  total_units: number;
  consumed_units: number;
  last_sync_at: string;
}

export interface DeviceBackup {
  id: number;
  device_id: number;
  vendor: string; // "windows_server_backup" | "veeam_agent" | ...
  job_name?: string | null;
  last_success_at?: string | null;
  last_failure_at?: string | null;
  last_error_message?: string | null;
  last_status: "success" | "failed" | "in_progress" | "unknown" | string;
  seen_at: string;
}

export interface NetworkDevice {
  id: number;
  customer_id: number;
  customer_name?: string | null;
  name: string;
  ip_address: string;
  kind: string;
  // "snmp" (poll OIDs) or "icmp" (ping up/down only — for non-SNMP gear).
  monitor_type?: string;
  poll_via_agent?: boolean;
  snmp_version: string;
  snmp_community?: string | null;
  snmp_port: number;
  enabled: boolean;
  // SNMP v3 (USM) — non-secret fields echoed by the API; has_* flags stored creds.
  snmp_v3_user?: string | null;
  snmp_v3_auth_protocol?: string | null;
  snmp_v3_priv_protocol?: string | null;
  has_snmp_v3_auth_password?: boolean;
  has_snmp_v3_priv_password?: boolean;
  has_ssh_credentials?: boolean;
  ssh_username?: string | null;
  ssh_port?: number;
  ssh_password?: string; // write-only (edit draft only)
  // write-only (set on the edit draft only; never returned by the API)
  snmp_v3_auth_password?: string;
  snmp_v3_priv_password?: string;
  last_polled_at?: string | null;
  last_status: "online" | "offline" | "error" | "unknown" | string;
  last_error?: string | null;
  // Plain-English reading of last_error. Absent when the device is healthy
  // AND when no rule matched the failure — an unrecognised error gets no
  // panel rather than a vague one, so last_error above is always rendered.
  error_diagnosis?: SnmpDiagnosis | null;
  sys_name?: string | null;
  sys_descr?: string | null;
  // sysObjectID and what the driver gate made of it.
  sys_object_id?: string | null;
  vendor_family?: string | null;
  vendor_label?: string | null;
  vendor_matched_on?: string | null;
  // Non-null when a config push would be refused, and why.
  config_push_refusal?: string | null;
  // The family an operator RECORDED by hand, for devices that cannot
  // identify themselves. Distinct from vendor_family, which is whatever is
  // in force — so the UI can say detection is being overridden rather than
  // echoing the override back as if the device had reported it.
  vendor_override?: string | null;
  sys_uptime_seconds?: number | null;
  consecutive_failures: number;
  maintenance_mode?: boolean;
  created_at: string;
  updated_at: string;
}

/** Why an SNMP poll failed, in terms a technician can act on. */
export interface SnmpDiagnosis {
  code: string;
  title: string;
  means: string;
  fix: string;
  /** Which end is at fault — the field that saves the most time. */
  side: "ours" | "device" | "network" | "unknown";
  /** True only when the operator can resolve it inside AeroGauge. */
  fixable_here: boolean;
  /** Device fields the operator most likely needs to change. */
  fields: string[];
}

export interface SwitchPort {
  if_index: number;
  name?: string | null;
  admin_up?: boolean | null;
  oper_up?: boolean | null;
  speed_mbps?: number | null;
  pvid?: number | null; // access / untagged VLAN
  tagged_vlans?: number[];
  in_bps?: number | null; // live throughput (diffed from octet counters)
  out_bps?: number | null;
}

export interface SwitchVlan {
  vlan_id: number;
  name?: string | null;
}

export interface SwitchDetail {
  id: number;
  name: string;
  ip_address: string;
  kind: string;
  last_status: string;
  last_polled_at?: string | null;
  sys_name?: string | null;
  sys_descr?: string | null;
  sys_uptime_seconds?: number | null;
  ports: SwitchPort[];
  vlans: SwitchVlan[];
}

export interface SoftwarePackage {
  id: number;
  name: string;
  description?: string | null;
  kind: "winget" | "msi" | "exe" | string;
  winget_id?: string | null;
  file_path?: string | null;
  file_size_bytes?: number | null;
  silent_args?: string | null;
  enabled: boolean;
  created_by_user_id?: number | null;
  created_at: string;
  updated_at: string;
}

export interface SoftwareDeployment {
  id: number;
  package_id?: number | null;
  package_name?: string | null;
  command_id?: number | null;
  device_id: number;
  device_hostname?: string | null;
  customer_id?: number | null;
  requested_by_user_id?: number | null;
  status: "queued" | "sent" | "running" | "installed" | "failed" | "timed_out" | string;
  queued_at: string;
  started_at?: string | null;
  completed_at?: string | null;
  output?: string | null;
  exit_code?: number | null;
  duration_ms?: number | null;
  error_message?: string | null;
}

export interface Script {
  id: number;
  /** NULL = a SHARED script, visible in every customer's library. */
  customer_id?: number | null;
  customer_name?: string | null;
  name: string;
  description?: string | null;
  language: "powershell" | "batch" | string;
  body: string;
  default_timeout_seconds: number;
  enabled: boolean;
  created_by_user_id?: number | null;
  created_at: string;
  updated_at: string;
}

export interface ScriptRun {
  id: number;
  script_id?: number | null;
  script_name?: string | null;
  command_id?: number | null;
  device_id: number;
  device_hostname?: string | null;
  customer_id?: number | null;
  requested_by_user_id?: number | null;
  status: "queued" | "sent" | "running" | "completed" | "failed" | "timed_out" | string;
  queued_at: string;
  started_at?: string | null;
  completed_at?: string | null;
  stdout?: string | null;
  stderr?: string | null;
  exit_code?: number | null;
  duration_ms?: number | null;
}

export interface BitlockerVolume {
  id: number;
  device_id: number;
  drive_letter: string;
  protection_status: string;
  encryption_method?: string | null;
  encryption_percentage?: number | null;
  key_id?: string | null;
  has_recovery_key: boolean;
  last_synced_at: string;
}

/** A queued/finished BitLocker action, so the outcome outlives the page.
 *  REPORTED: "I clicked away from the screen and it did not save its status
 *  or progress. I had to click the button again." */
export interface BitlockerActivity {
  command_id: number;
  action: "enable" | "resume" | "disable" | string;
  drive_letter?: string | null;
  status: string;
  pending: boolean;
  changed?: boolean | null;
  reason?: string | null;
  /** This outcome has aged out of being feedback (server-computed, 24h).
   *  Still returned so history is not hidden from an API consumer — but the
   *  device page must not banner it, because a days-old failure nobody is
   *  waiting on reads as a live fault. */
  stale?: boolean;
  requested_at: string;
  completed_at?: string | null;
}

/** An agent-version pin: this device is deliberately held off the rollout.
 *
 *  Mirrors AgentPinOut in routers/devices.py. `behind_fleet_target` is the
 *  field that matters — a pin AT the fleet target is harmless, a pin BEHIND
 *  it means the machine is missing whatever the newer agent fixed.
 */
export interface AgentPin {
  device_id: number;
  hostname?: string | null;
  customer_name?: string | null;
  running_version?: string | null;
  pinned_version?: string | null;
  reason?: string | null;
  pinned_at?: string | null;
  pinned_by?: string | null;
  is_ad_runner: boolean;
  status?: string | null;
  behind_fleet_target: boolean;
  fleet_target?: string | null;
  age_days?: number | null;
}

export interface BitlockerKeyReveal {
  id: number;
  drive_letter: string;
  key_id?: string | null;
  recovery_password: string;
}

export interface MaintenanceWindow {
  id: number;
  customer_id: number;
  enabled: boolean;
  days_of_week: number[]; // Monday=0 .. Sunday=6
  start_time: string; // "HH:MM" 24h
  end_time: string; // "HH:MM" 24h
  timezone: string;
  allow_reboot: boolean;
  reboot_deferral_seconds: number;
  reboot_message?: string | null;
  updated_at: string;
}

export interface DeviceCompliance {
  device_id: number;
  hostname: string;
  customer_id?: number | null;
  customer_name?: string | null;
  device_status: string;
  pending: number;
  pending_critical: number;
  pending_important: number;
  pending_reboot: boolean;
  oldest_pending_age_days?: number | null;
  installed_last_30d: number;
  failed_last_30d: number;
  is_behind: boolean;
}

export interface CustomerCompliance {
  customer_id: number;
  customer_name: string;
  total_devices: number;
  devices_up_to_date: number;
  devices_behind: number;
  /** Pending updates, but none critical or important. */
  devices_optional_only: number;
  /** Fully patched, owes only a restart. */
  devices_reboot_only: number;
  /**
   * up_to_date + behind + optional_only + reboot_only === total_devices.
   *
   * The first two alone looked like a partition and were not — "behind" means
   * critical or important is pending, "up to date" means nothing pending AND
   * no restart owed — so a machine with only optional updates, or one waiting
   * only to reboot, appeared in neither tile. On a six-device customer that
   * was four machines with no tile at all.
   *
   * devices_pending_reboot is NOT part of that sum: it counts every device
   * owing a restart, including ones already counted as behind.
   */
  devices_pending_reboot: number;
  pending: number;
  pending_critical: number;
  pending_important: number;
  installed_last_30d: number;
  failed_last_30d: number;
  oldest_pending_age_days?: number | null;
}

export interface CustomerComplianceDetail extends CustomerCompliance {
  devices: DeviceCompliance[];
}

export interface ComplianceSummary {
  total_customers: number;
  total_devices: number;
  devices_up_to_date: number;
  devices_behind: number;
  /** Pending updates, but none critical or important. */
  devices_optional_only: number;
  /** Fully patched, owes only a restart. */
  devices_reboot_only: number;
  /**
   * up_to_date + behind + optional_only + reboot_only === total_devices.
   *
   * The first two alone looked like a partition and were not — "behind" means
   * critical or important is pending, "up to date" means nothing pending AND
   * no restart owed — so a machine with only optional updates, or one waiting
   * only to reboot, appeared in neither tile. On a six-device customer that
   * was four machines with no tile at all.
   *
   * devices_pending_reboot is NOT part of that sum: it counts every device
   * owing a restart, including ones already counted as behind.
   */
  devices_pending_reboot: number;
  pending: number;
  pending_critical: number;
  pending_important: number;
  installed_last_30d: number;
  failed_last_30d: number;
  oldest_pending_age_days?: number | null;
}

export interface PatchPolicy {
  id: number;
  customer_id?: number | null;
  auto_approve_severities: string[];
  auto_approve_categories: string[];
  decline_severities: string[];
  decline_categories: string[];
  approve_kbs: string[];
  decline_kbs: string[];
  updated_at: string;
}

export interface SLAPolicy {
  id: number;
  customer_id?: number | null;
  response_low_minutes: number;
  response_medium_minutes: number;
  response_high_minutes: number;
  response_urgent_minutes: number;
  resolution_low_minutes: number;
  resolution_medium_minutes: number;
  resolution_high_minutes: number;
  resolution_urgent_minutes: number;
  updated_at: string;
}

export interface TicketSLAStatus {
  ticket_id: number;
  priority: string;
  response_target_minutes: number;
  response_elapsed_minutes: number;
  response_met_at: string | null;
  response_breached_at: string | null;
  response_pct_consumed: number;
  response_at_risk: boolean;
  resolution_target_minutes: number;
  resolution_elapsed_minutes: number;
  resolution_met_at: string | null;
  resolution_breached_at: string | null;
  resolution_pct_consumed: number;
  resolution_at_risk: boolean;
}

export interface DeviceWarranty {
  device_id: number;
  vendor: string;
  service_tag: string | null;
  start_date: string | null;
  end_date: string | null;
  service_level: string | null;
  status: "active" | "expired" | "unknown";
  last_synced_at: string | null;
  last_sync_attempt_at: string | null;
  last_sync_error: string | null;
}

export interface Credential {
  id: number;
  customer_id: number;
  category: string;
  label: string;
  username: string | null;
  url: string | null;
  notes: string | null;
  created_by_user_id: number | null;
  last_accessed_at: string | null;
  last_accessed_by_user_id: number | null;
  created_at: string;
  updated_at: string;
}

export interface CredentialReveal {
  id: number;
  label: string;
  username: string | null;
  secret: string;
  revealed_at: string;
}

export interface RunbookStep {
  title: string;
  body: string;
}

export interface Runbook {
  id: number;
  customer_id: number | null;
  visibility: "internal" | "customer";
  title: string;
  description: string | null;
  steps: RunbookStep[];
  created_by_user_id: number | null;
  updated_by_user_id: number | null;
  created_at: string;
  updated_at: string;
}

export interface PortalRunbookSummary {
  id: number;
  title: string;
  description: string | null;
  customer_id: number | null;
  updated_at: string;
}

export interface PortalRunbookDetail {
  id: number;
  title: string;
  description: string | null;
  steps: RunbookStep[];
  customer_id: number | null;
  updated_at: string;
}

export interface AtRiskTicket {
  ticket_id: number;
  title: string;
  customer_id: number | null;
  customer_name: string | null;
  priority: string;
  status: string;
  risk_kind: "response" | "resolution";
  pct_consumed: number;
  target_minutes: number;
  elapsed_minutes: number;
}

export interface DevicePatchSummary {
  pending: number;
  critical: number;
  important: number;
  reboot_required: number;
  installed: number;
  failed: number;
}

export interface CalendarEvent {
  id: number;
  user_id: number;
  title: string;
  description?: string;
  starts_at: string;
  ends_at?: string;
  all_day: boolean;
  ticket_id?: number;
  ticket_title?: string;
  customer_id?: number;
  customer_name?: string;
  remind_minutes_before?: number;
  remind_at?: string;
  reminded_at?: string;
  created_at: string;
}
